import { extractErrorMessage } from "../utils/acceptSuggestions";
/**
 * IT-DRP Plan Wizard - Dedicated Turbo-Tax Style Wizard
 * For Business Application Owners to create IT Disaster Recovery Plans
 * Follows the standard Recovery Plan Task format
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { RecoveryTaskTable } from "../components/tables/RecoveryTaskTable";
import {
  Server,
  Database,
  Cloud,
  Shield,
  Activity,
  Clock,
  Target,
  Users,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Save,
  Send,
  CheckCircle2,
  AlertTriangle,
  Zap,
  Timer,
  Snowflake,
  Wrench,
  Info,
  FileText,
  Network,
  ClipboardCheck,
} from "lucide-react";

// Recovery strategy options
const RECOVERY_STRATEGIES = [
  { 
    id: "hot_standby", 
    name: "Hot Standby", 
    rtoRange: "0-4 hours",
    icon: Zap,
    color: "bg-red-100 text-red-700 border-red-200",
    description: "Active-Active or Active-Passive. Immediate failover capability.",
    cost: "High"
  },
  { 
    id: "warm_standby", 
    name: "Warm Standby", 
    rtoRange: "4-24 hours",
    icon: Timer,
    color: "bg-amber-100 text-amber-700 border-amber-200",
    description: "Pre-configured standby environment. Requires data sync.",
    cost: "Medium"
  },
  { 
    id: "cold_site", 
    name: "Cold Site", 
    rtoRange: "24-72 hours",
    icon: Snowflake,
    color: "bg-blue-100 text-blue-700 border-blue-200",
    description: "Infrastructure only. Requires full restoration.",
    cost: "Low"
  },
  { 
    id: "rebuild", 
    name: "Rebuild/Restore", 
    rtoRange: "72+ hours",
    icon: Wrench,
    color: "bg-slate-100 text-slate-700 border-slate-200",
    description: "Complete rebuild from backups and documentation.",
    cost: "Lowest"
  },
];

const WIZARD_STEPS = [
  { id: 1, title: "Application Selection", icon: Server, description: "Select application to protect" },
  { id: 2, title: "Recovery Strategy", icon: Target, description: "Define recovery approach" },
  { id: 3, title: "RTO/RPO Targets", icon: Clock, description: "Set recovery objectives" },
  { id: 4, title: "Recovery Tasks", icon: FileText, description: "Define task sequence" },
  { id: 5, title: "Resources & Prerequisites", icon: Users, description: "Required resources" },
  { id: 6, title: "Validation & Review", icon: CheckCircle2, description: "Review and submit" },
];

export default function ITDRPWizardPage() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  
  // Plan Data
  const [planData, setPlanData] = useState({
    plan_name: "",
    plan_type: "it_drp",
    application_id: profileId || "",
    recovery_strategy: "",
    rto_hours: 4,
    rpo_hours: 1,
    recovery_tasks: [],
    prerequisites: "",
    resource_requirements: "",
    validation_steps: [],
    notes: "",
  });
  
  // New task form
  const [newTask, setNewTask] = useState({
    task_id: "T001",
    actual_task: "",
    task_owner: "",
    predecessor_task_id: "",
    estimated_duration: "30 mins",
    estimated_duration_mins: 30,
    notes: "",
  });
  
  // New validation step
  const [newValidation, setNewValidation] = useState("");

  // Approver selection state
  const [orgUsers, setOrgUsers] = useState([]);
  const [selectedApprover, setSelectedApprover] = useState(null);

  const isBusinessAppOwner = user?.role === "process_manager" || user?.role === "process_manager" || user?.role === "business_app_owner";
  const isAdmin = user?.role === "bcm_manager" || user?.role === "department_head" || user?.role === "it_drp_manager" || user?.role === "admin";

  // Fetch available applications
  const fetchApplications = useCallback(async () => {
    try {
      const response = await api.get("/application-profile/my-tasks");
      const completed = (response.data.data?.tasks || []).filter(t => t.status === "completed");
      setApplications(completed);
      
      // If profileId provided, select that app
      if (profileId) {
        const app = completed.find(a => a.id === profileId);
        if (app) {
          setSelectedApp(app);
          setPlanData(prev => ({
            ...prev,
            application_id: app.id,
            plan_name: `IT-DRP: ${app.application_name}`,
          }));
        }
      }
    } catch (error) {
      console.error("Failed to fetch applications:", error);
    } finally {
      setLoading(false);
    }
  }, [api, profileId]);

  useEffect(() => {
    fetchApplications();
    // Fetch org users for approver selection
    api.get("/recovery-tasks/org-users-for-approver")
      .then(res => setOrgUsers(res.data.users || []))
      .catch(() => {});
  }, [fetchApplications, api]);

  // Add recovery task
  const addRecoveryTask = () => {
    if (!newTask.actual_task || !newTask.task_owner) {
      toast.error("Task description and owner are required");
      return;
    }
    
    const taskId = newTask.task_id || `T${String(planData.recovery_tasks.length + 1).padStart(3, '0')}`;
    
    setPlanData(prev => ({
      ...prev,
      recovery_tasks: [
        ...prev.recovery_tasks,
        { ...newTask, task_id: taskId, status: "not_started" }
      ]
    }));
    
    // Reset and auto-increment task ID
    setNewTask({
      task_id: `T${String(planData.recovery_tasks.length + 2).padStart(3, '0')}`,
      actual_task: "",
      task_owner: "",
      predecessor_task_id: taskId,
      estimated_duration: "30 mins",
      estimated_duration_mins: 30,
      notes: "",
    });
    
    toast.success("Task added");
  };

  // Remove recovery task
  const removeTask = (index) => {
    setPlanData(prev => ({
      ...prev,
      recovery_tasks: prev.recovery_tasks.filter((_, i) => i !== index)
    }));
  };

  // Add validation step
  const addValidationStep = () => {
    if (!newValidation.trim()) return;
    setPlanData(prev => ({
      ...prev,
      validation_steps: [...prev.validation_steps, newValidation.trim()]
    }));
    setNewValidation("");
  };

  // Navigation
  const nextStep = () => {
    if (currentStep < WIZARD_STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Save draft
  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.post("/it-drp/plans/draft", {
        plan_name: planData.plan_name,
        application_id: planData.application_id,
        recovery_strategy: planData.recovery_strategy,
        rto_hours: planData.rto_hours,
        rpo_hours: planData.rpo_hours,
        recovery_tasks: planData.recovery_tasks,
        prerequisites: planData.prerequisites,
        resource_requirements: planData.resource_requirements,
        validation_steps: planData.validation_steps,
        notes: planData.notes,
        status: "draft",
      });
      toast.success("Draft saved successfully");
    } catch (error) {
      console.error("Save draft error:", error);
      toast.error(extractErrorMessage(error, "Failed to save draft"));
    } finally {
      setSaving(false);
    }
  };

  // Submit plan
  const submitPlan = async () => {
    if (planData.recovery_tasks.length === 0) {
      toast.error("Please add at least one recovery task");
      return;
    }
    
    if (!planData.recovery_strategy) {
      toast.error("Please select a recovery strategy");
      return;
    }
    
    setSaving(true);
    try {
      await api.post("/it-drp/plans", {
        plan_name: planData.plan_name,
        application_id: planData.application_id,
        recovery_strategy: planData.recovery_strategy,
        rto_hours: planData.rto_hours,
        rpo_hours: planData.rpo_hours,
        recovery_tasks: planData.recovery_tasks,
        prerequisites: planData.prerequisites,
        resource_requirements: planData.resource_requirements,
        validation_steps: planData.validation_steps,
        notes: planData.notes,
        status: "submitted",
        approver_user_id: selectedApprover?.id || null,
        approver_email: selectedApprover?.email || null,
        approver_name: selectedApprover?.name || null,
      });
      toast.success(selectedApprover
        ? `IT-DRP Plan submitted — ${selectedApprover.name} will review`
        : "IT-DRP Plan submitted successfully!"
      );
      navigate("/my-recovery-work");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(extractErrorMessage(error, "Failed to submit plan"));
    } finally {
      setSaving(false);
    }
  };

  const progress = Math.round((currentStep / WIZARD_STEPS.length) * 100);

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Application Selection
        return (
          <div className="space-y-6">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Server className="w-5 h-5 text-purple-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-purple-900">Select Application</h4>
                  <p className="text-sm text-purple-700">
                    Choose the application for which you want to create an IT-DRP Plan.
                    Only applications with completed profiles are available.
                  </p>
                </div>
              </div>
            </div>
            
            {applications.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <Database className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-600">No completed application profiles found</p>
                <p className="text-sm text-slate-500 mt-2">Complete an Application Profile first</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => navigate("/my-application-profiles")}
                >
                  Go to My Application Profiles
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {applications.map((app) => (
                  <Card
                    key={app.id}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      selectedApp?.id === app.id 
                        ? "border-2 border-purple-500 bg-purple-50" 
                        : "hover:border-purple-300"
                    }`}
                    onClick={() => {
                      setSelectedApp(app);
                      setPlanData(prev => ({
                        ...prev,
                        application_id: app.id,
                        plan_name: `IT-DRP: ${app.application_name}`,
                      }));
                    }}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          app.deployment_type === "cloud" || app.deployment_type === "saas"
                            ? "bg-blue-100"
                            : "bg-slate-100"
                        }`}>
                          {app.deployment_type === "cloud" || app.deployment_type === "saas" 
                            ? <Cloud className="w-5 h-5 text-blue-600" />
                            : <Server className="w-5 h-5 text-slate-600" />
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{app.application_name}</p>
                          <p className="text-sm text-slate-500 truncate">
                            {app.deployment_type?.replace("_", " ").toUpperCase() || "Unknown"}
                          </p>
                        </div>
                        {selectedApp?.id === app.id && (
                          <CheckCircle2 className="w-5 h-5 text-purple-600" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            
            {selectedApp && (
              <div className="space-y-2">
                <Label>Plan Name</Label>
                <Input
                  value={planData.plan_name}
                  onChange={(e) => setPlanData(prev => ({ ...prev, plan_name: e.target.value }))}
                  placeholder="IT-DRP: Application Name"
                />
              </div>
            )}
          </div>
        );
        
      case 2: // Recovery Strategy
        return (
          <div className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Target className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-900">Recovery Strategy Selection</h4>
                  <p className="text-sm text-amber-700">
                    Select the recovery strategy that aligns with the application's criticality and RTO requirements.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {RECOVERY_STRATEGIES.map((strategy) => {
                const Icon = strategy.icon;
                return (
                  <Card
                    key={strategy.id}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      planData.recovery_strategy === strategy.id
                        ? `border-2 ${strategy.color}`
                        : "hover:border-slate-300"
                    }`}
                    onClick={() => setPlanData(prev => ({ ...prev, recovery_strategy: strategy.id }))}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${strategy.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium">{strategy.name}</h4>
                            {planData.recovery_strategy === strategy.id && (
                              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            )}
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{strategy.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              RTO: {strategy.rtoRange}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              Cost: {strategy.cost}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
        
      case 3: // RTO/RPO Targets
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900">Recovery Time Objectives</h4>
                  <p className="text-sm text-blue-700">
                    Define the maximum acceptable downtime (RTO) and data loss (RPO) for this application.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="w-5 h-5 text-red-600" />
                    RTO (Recovery Time Objective)
                  </CardTitle>
                  <CardDescription>Maximum acceptable downtime</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label>Hours</Label>
                      <Input
                        type="number"
                        min="0"
                        value={planData.rto_hours}
                        onChange={(e) => setPlanData(prev => ({ ...prev, rto_hours: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                    <div className="text-sm text-slate-600">
                      = {planData.rto_hours < 24 
                        ? `${planData.rto_hours} hours` 
                        : `${Math.floor(planData.rto_hours / 24)} days ${planData.rto_hours % 24} hours`}
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Database className="w-5 h-5 text-blue-600" />
                    RPO (Recovery Point Objective)
                  </CardTitle>
                  <CardDescription>Maximum acceptable data loss</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label>Hours</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={planData.rpo_hours}
                        onChange={(e) => setPlanData(prev => ({ ...prev, rpo_hours: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                    <div className="text-sm text-slate-600">
                      = {planData.rpo_hours < 1 
                        ? `${planData.rpo_hours * 60} minutes` 
                        : `${planData.rpo_hours} hours`} of potential data loss
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        );
        
      case 4: // Recovery Tasks - THE MAIN TURBO-TAX TABLE
        return (
          <div className="space-y-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-emerald-900">Recovery Task Sequence</h4>
                  <p className="text-sm text-emerald-700">
                    Define the step-by-step recovery tasks. Start Time and Completion Time will be recorded during actual DR exercises.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Recovery Tasks — Editable Table with Sort/Filter */}
            <RecoveryTaskTable
              tasks={planData.recovery_tasks}
              onTasksChange={(updated) => setPlanData(prev => ({ ...prev, recovery_tasks: updated }))}
              userName={user?.name || ""}
              emptyMessage="No tasks added yet. Click 'Add Task' to define your first recovery step."
            />
          </div>
        );
        
      case 5: // Resources & Prerequisites
        return (
          <div className="space-y-6">
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-sky-700 mt-0.5" />
                <div>
                  <h4 className="font-medium text-sky-900">Resources & Prerequisites</h4>
                  <p className="text-sm text-sky-700">
                    Define what's needed before recovery can begin and what resources are required.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Prerequisites</Label>
                <Textarea
                  placeholder="List any prerequisites that must be in place before recovery can begin..."
                  value={planData.prerequisites}
                  onChange={(e) => setPlanData(prev => ({ ...prev, prerequisites: e.target.value }))}
                  rows={4}
                />
              </div>
              
              <div>
                <Label>Resource Requirements</Label>
                <Textarea
                  placeholder="List required resources (hardware, software, personnel, access credentials)..."
                  value={planData.resource_requirements}
                  onChange={(e) => setPlanData(prev => ({ ...prev, resource_requirements: e.target.value }))}
                  rows={4}
                />
              </div>
              
              <div>
                <Label>Additional Notes</Label>
                <Textarea
                  placeholder="Any other important information..."
                  value={planData.notes}
                  onChange={(e) => setPlanData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                />
              </div>
            </div>
          </div>
        );
        
      case 6: // Validation & Review
        return (
          <div className="space-y-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-emerald-900">Final Review</h4>
                  <p className="text-sm text-emerald-700">
                    Review your IT-DRP plan and add validation steps to verify successful recovery.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Plan Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">Plan Name</Label>
                    <p className="font-medium">{planData.plan_name || "—"}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Application</Label>
                    <p className="font-medium">{selectedApp?.application_name || "—"}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Recovery Strategy</Label>
                    <p className="font-medium">
                      {RECOVERY_STRATEGIES.find(s => s.id === planData.recovery_strategy)?.name || "—"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500">RTO / RPO</Label>
                    <p className="font-medium">{planData.rto_hours} hours / {planData.rpo_hours} hours</p>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-500">Recovery Tasks</Label>
                  <p className="font-medium">{planData.recovery_tasks.length} tasks defined</p>
                </div>
              </CardContent>
            </Card>
            
            {/* Validation Steps */}
            <Card>
              <CardHeader>
                <CardTitle>Validation Steps</CardTitle>
                <CardDescription>How will you verify the recovery was successful?</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {planData.validation_steps.length > 0 && (
                  <ul className="space-y-2">
                    {planData.validation_steps.map((step, index) => (
                      <li key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        <span className="flex-1">{step}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500"
                          onClick={() => setPlanData(prev => ({
                            ...prev,
                            validation_steps: prev.validation_steps.filter((_, i) => i !== index)
                          }))}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
                
                <div className="flex gap-2">
                  <Input
                    placeholder="Add validation step (e.g., 'Verify database connectivity')"
                    value={newValidation}
                    onChange={(e) => setNewValidation(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addValidationStep()}
                  />
                  <Button variant="outline" onClick={addValidationStep}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Approver Selection */}
            <Card className="border-sky-200 bg-sky-50/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-indigo-800">
                  <ClipboardCheck className="w-5 h-5" />
                  Designate Your Approver / Manager
                </CardTitle>
                <CardDescription className="text-sky-700">
                  Select the person who will review and approve this IT-DRP Plan. This should be your IT function manager or application owner.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select
                  value={selectedApprover?.id || "none"}
                  onValueChange={(val) => {
                    if (val === "none") {
                      setSelectedApprover(null);
                    } else {
                      const u = orgUsers.find(u => u.id === val);
                      setSelectedApprover(u || null);
                    }
                  }}
                >
                  <SelectTrigger data-testid="approver-select">
                    <SelectValue placeholder="Select an approver..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific approver</SelectItem>
                    {orgUsers.filter(u => u.id !== user?.id).map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.email}){u.department ? ` — ${u.department}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedApprover && (
                  <div className="flex items-center gap-2 text-sm text-sky-700 bg-sky-100 p-2 rounded mt-3">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Approver: <strong>{selectedApprover.name}</strong> ({selectedApprover.email})</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
        
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-in" data-testid="it-drp-wizard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-purple-100 text-purple-700">IT-DRP</Badge>
            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">ISO 22301:8.4</Badge>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">IT Disaster Recovery Plan Wizard</h1>
          <p className="text-slate-600">Create a comprehensive IT-DRP with task-based recovery steps</p>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Progress */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-slate-600">Step {currentStep} of {WIZARD_STEPS.length}</span>
            <span className="text-sm font-medium text-purple-600">{progress}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
          
          {/* Step indicators */}
          <div className="flex justify-between mt-4">
            {WIZARD_STEPS.map((step) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.id}
                  className={`flex flex-col items-center ${
                    currentStep === step.id ? "text-purple-600" :
                    currentStep > step.id ? "text-emerald-600" : "text-slate-400"
                  }`}
                >
                  <div className={`p-2 rounded-full ${
                    currentStep === step.id ? "bg-purple-100" :
                    currentStep > step.id ? "bg-emerald-100" : "bg-slate-100"
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="text-xs mt-1 hidden md:block">{step.title}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Step Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {React.createElement(WIZARD_STEPS[currentStep - 1].icon, { className: "w-5 h-5" })}
            {WIZARD_STEPS[currentStep - 1].title}
          </CardTitle>
          <CardDescription>{WIZARD_STEPS[currentStep - 1].description}</CardDescription>
        </CardHeader>
        <CardContent>
          {renderStepContent()}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={currentStep === 1}
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={saveDraft} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            Save Draft
          </Button>
          
          {currentStep === WIZARD_STEPS.length ? (
            <Button 
              onClick={submitPlan} 
              disabled={saving || planData.recovery_tasks.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Send className="w-4 h-4 mr-2" />
              Submit Plan
            </Button>
          ) : (
            <Button onClick={nextStep} className="bg-purple-600 hover:bg-purple-700">
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
